import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { User, UserProfile } from '../types';
import { FaWindowClose, FaGoogle, FaMailBulk } from 'react-icons/fa';
import { emailPasswordSignIn, emailPasswordSignUp, signInWithGoogle, signOut } from '../Firebase/utils';
import { setUser } from '../store/features/UserSlice';
import Modal from './Modal';
import { useLocation, useNavigate } from 'react-router-dom';

import logo from '../assets/images/logo-blue-1.png';
import NavButtons from './NavButtons';
import TextField from './TextField';
import { isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink } from 'firebase/auth';
import { Sign } from 'crypto';
import SignUpWithEmailComponent from './SignUpWithEmailComponent';
import SignInWithEmailComponent from './SignInWithEmailComponent';
import { resetSurvey } from '../store/features/CreateSurveySlice';

const NavBar = () => {

    const user: UserProfile = useAppSelector((state) => state.user.user);
    const navigate = useNavigate();
    const location = useLocation();
    const routePath = location.pathname;
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const dispatch = useAppDispatch();
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
    const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
    const [infoMessage, setInfoMessage] = useState<string>('Enter your email and we\'ll send you a link');
    const [isOpen, setIsOpen] = useState(false);
    const buttonText = user.uid ? 'Sign Out' : 'Sign In';

    const handleAuth = async () => {
        if(user.uid === ''){
            setIsOpen(false);
            setIsSignInModalOpen(true);
            setIsSignUpModalOpen(false);
        }else{
            await signOut();
            dispatch(setUser({uid: '', email: '', displayName: '', userIdToken: ''}));
            navigate('/');
            setIsOpen(false);
        }
    }

    const hamburgerToggle = () => {
      setIsOpen(!isOpen);
    }

    const handleGoogleSignIn = async () => {
        const {...user} = await signInWithGoogle() as User;
        if (user) {
            dispatch(setUser(user));
            setIsSignInModalOpen(false);
            setIsOpen(false);
        }
    };

    const handleSignInWithEmailPassword = async () => {
      const {...user} = await emailPasswordSignIn(email, password);
      if (user) {
        dispatch(setUser(user));
        setIsSignInModalOpen(false);
        setIsOpen(false);
      }
    }

  useEffect(() => {
      if (user.uid !== '') {
          setIsSignInModalOpen(false);
          setIsOpen(false);
      } 
  }, [user]);

    return (
        <div className='flex w-full justify-between p-5 bg-[#131212] shadow-md shadow-black relative h-20'>
            <div onClick={() => navigate('/')} className='flex items-center md:w-1/3 gap-2 hover:cursor-pointer'>
                <img src={logo} className='h-full bg-slate-300 rounded-full'/>
                <h1 className='text-3xl text-slate-300 font-Velocity'>SURV<span className='text-[#3da8e2]'>E</span>Y M<span className='text-[#3da8e2]'>3</span></h1>
            </div>
            <div className='flex md:w-full space-x-5'>
                <div className='hidden md:flex-grow md:flex gap-5 justify-end'>
                    <NavButtons user={user} setIsSignInModalOpen={setIsSignInModalOpen} path={'/'} label={'Home'} className={`${location.pathname !== '/' && 'text-slate-300'}`}/>
                    <NavButtons callback={() => dispatch(resetSurvey())} user={user} setIsSignInModalOpen={setIsSignInModalOpen} path={'/survey-builder'} label={'Survey Builder'}/>
                    <NavButtons user={user} setIsSignInModalOpen={setIsSignInModalOpen} path={'/manage-surveys'} label={'Manage Surveys'}/>
                    <NavButtons user={user} setIsSignInModalOpen={setIsSignInModalOpen} path={'/complete-survey'} label={'Complete Survey'}/>
                </div>
                <button onClick={handleAuth} className='tooltip hidden md:flex md:w-fit p-2 text-[#3da8e2] border border-[#3da8e2] rounded-lg font-bold font-BardotBold tracking-widest hover:border-slate-300 hover:text-slate-300'>
                    {user.uid && <div className='tooltip-text'>Signed In as {user.email}</div>}
                    {buttonText}
                </button>
                <button onClick={hamburgerToggle} className='flex md:hidden flex-col justify-center items-center w-10 h-10 bg-[#131212] border border-[#3f3f3f] rounded-md shadow-md hover:bg-slate-700'>
                    <span className={`block w-6 h-0.5 bg-[#3da8e2] mb-1 transition-transform duration-300 ${isOpen ? 'rotate-45 translate-y-2 bg-[#3da8e2]' : ''}`}></span>
                    <span className={`block w-6 h-0.5 bg-[#3da8e2] mb-1 transition-opacity duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
                    <span className={`block w-6 h-0.5 bg-[#3da8e2] transition-transform duration-300 ${isOpen ? '-rotate-45 -translate-y-1 bg-[#3da8e2]' : ''}`}></span>
                </button>
                <section>
                    <div className={`flex flex-col absolute top-20 -right-2/3 h-screen w-2/3 z-50 bg-[#131212] shadow-md shadow-black p-5 transition-transform duration-300 ${isOpen ? '-translate-x-full' : 'hidden'}`}>
                        <div>
                            <NavButtons user={user} setIsSignInModalOpen={setIsSignInModalOpen} hamburgerToggle={hamburgerToggle} path={'/'} label={'Home'} className={`flex w-full justify-center ${location.pathname !== '/' && 'text-slate-300 border-slate-300'} border border-slate-600 mb-6 shadow-black shadow-md`}/>
                            <NavButtons callback={() => dispatch(resetSurvey())} user={user} setIsSignInModalOpen={setIsSignInModalOpen} hamburgerToggle={hamburgerToggle} path={'/survey-builder'} label={'Survey Builder'} className={`flex w-full justify-center border border-slate-600 mb-6 shadow-black shadow-md`}/>
                            <NavButtons user={user} setIsSignInModalOpen={setIsSignInModalOpen} hamburgerToggle={hamburgerToggle} path={'/manage-surveys'} label={'Manage Surveys'} className={`flex w-full justify-center border border-slate-600 mb-6 shadow-black shadow-md`}/>
                            <NavButtons user={user} setIsSignInModalOpen={setIsSignInModalOpen} hamburgerToggle={hamburgerToggle} path={'/complete-survey'} label={'Complete Survey'} className={`flex w-full justify-center border border-slate-600 mb-6 shadow-black shadow-md`}/>
                        </div>
                        <button onClick={handleAuth} className='flex w-full justify-center p-2 text-[#3da8e2] border border-slate-600 rounded-lg font-bold font-BardotBold tracking-widest'>
                            {buttonText}
                        </button>
                        <p className='text-white'>Signed In as {user.email}</p>
                    </div>
                </section>
                {/* <Modal title='Please Sign In' onClose={() => { setIsSignInModalOpen(false) }} openState={isSignInModalOpen &&  !isSignUpModalOpen} left={'-left-5'}>
                  <TextField label={'Email'} placeholder={'Enter your email'} inputValue={email} setInputValue={(value) => {setEmail(value)}}/>
                  <TextField type='password' label={'Password'} placeholder={'Password'} inputValue={password} setInputValue={(value) => {setPassword(value)}}/>
                        <button onClick={() => handleSignInWithEmailPassword()} className='flex flex-row gap-10 w-full items-center justify-center mt-10 py-2 text-lg md:text-xl uppercase font-SoulGaze tracking-wider text-slate-300 border border-slate-400 rounded-md'>
                            <p className='flex gap-4 items-center text-[#3da8e2] font-BardotBold tracking-[0.25rem]'>Login</p>
                        </button>
                        <button onClick={() => handleGoogleSignIn()} className='flex flex-row gap-10 w-full items-center justify-center mt-10 py-2 text-lg md:text-xl uppercase font-SoulGaze tracking-wider text-slate-300 border border-slate-400 rounded-md'>
                            <p className='flex gap-4 items-center text-[#3da8e2] font-BardotBold tracking-[0.25rem]'><FaGoogle className='text-2xl'/>Signin With Google</p>
                        </button>
                        <p className="flex justify-end pt-4">Don't have an account?<span onClick={() => setIsSignUpModalOpen(true)} className='text-[#3da8e2] pl-2 hover:cursor-pointer'>Create Account</span></p>
                </Modal> */}
                <SignInWithEmailComponent isSignInModalOpen={isSignInModalOpen} isSignUpModalOpen={isSignUpModalOpen} setIsSignUpModalOpen={setIsSignUpModalOpen} onClose={() => { setIsSignInModalOpen(false) }}/>
                <SignUpWithEmailComponent isSignUpModalOpen={isSignUpModalOpen} onClose={() => setIsSignUpModalOpen(false)}/>
                {/* <Modal title='Create Account' onClose={() => { setIsSignUpModalOpen(false) }} openState={isSignUpModalOpen} left={'-left-5'}>
                  <p>{infoMessage}</p>
                  <TextField label={'Email'} placeholder={'Enter your email'} inputValue={email} setInputValue={(value) => {setEmail(value)}}/>
                  <TextField type='password' label={'Password'} placeholder={'Password'} inputValue={password} setInputValue={(value) => {setPassword(value)}}/>
                  <TextField type='password' label={'Confirm Password'} placeholder={'Confirm Password'} inputValue={confirmPassword} setInputValue={(value) => {setConfirmPassword(value)}}/>
                        <button onClick={() => handleSignUpWithEmailPassword()} className='flex flex-row gap-10 w-full items-center justify-center mt-10 py-2 text-lg md:text-xl uppercase font-SoulGaze tracking-wider text-slate-300 border border-slate-400 rounded-md'>
                            <p className='flex gap-4 items-center text-[#3da8e2] font-BardotBold tracking-[0.25rem]'><FaMailBulk className='text-2xl'/>Create Account</p>
                        </button>
                        <button onClick={() => handleGoogleSignIn()} className='flex flex-row gap-10 w-full items-center justify-center mt-10 py-2 text-lg md:text-xl uppercase font-SoulGaze tracking-wider text-slate-300 border border-slate-400 rounded-md'>
                            <p className='flex gap-4 items-center text-[#3da8e2] font-BardotBold tracking-[0.25rem]'><FaGoogle className='text-2xl'/>Signin With Google</p>
                        </button>
                </Modal> */}
            </div>
        </div>
    );
    };
    export default NavBar;
